import React, { useState, useEffect } from 'react';
import { Gift, TrendingDown, Clock, CheckCircle } from 'lucide-react';
import api from '../api/axios';
import { toast } from 'sonner';

const PreApprovedOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await api.get('/offers/my-offers');
      setOffers(response.data);
    } catch (error) {
      console.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimOffer = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/claim`);
      toast.success('Offer claimed! Our team will contact you shortly.');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim offer');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#354F37] mx-auto"></div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm text-center">
        <Gift className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-[#222222] mb-2">No Offers Available</h3>
        <p className="text-[#444444]">
          Pre-approved offers will appear here based on your profile and application history.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Gift className="text-[#354F37]" size={28} />
        <div>
          <h3 className="text-xl font-bold text-[#222222]">Pre-Approved Offers</h3>
          <p className="text-sm text-[#444444]">Exclusive deals just for you</p>
        </div>
      </div>

      {offers.map((offer) => {
        const isExpired = new Date(offer.valid_until) < new Date();
        
        return (
          <div
            key={offer.id}
            className={`bg-white p-6 rounded-xl border-2 shadow-sm ${
              isExpired ? 'border-gray-300 opacity-60' : 'border-[#354F37]'
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-xl font-bold text-[#222222] mb-1">{offer.title}</h4>
                <p className="text-[#444444]">{offer.description}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isExpired 
                  ? 'bg-gray-200 text-gray-600' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {isExpired ? 'Expired' : 'Active'}
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {offer.loan_amount && (
                <div className="flex items-center gap-2">
                  <TrendingDown className="text-[#354F37]" size={20} />
                  <div>
                    <p className="text-xs text-[#444444]">Loan Amount</p>
                    <p className="font-semibold text-[#222222]">
                      ₹{offer.loan_amount.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              )}
              
              {offer.interest_rate && (
                <div className="flex items-center gap-2">
                  <TrendingDown className="text-[#354F37]" size={20} />
                  <div>
                    <p className="text-xs text-[#444444]">Interest Rate</p>
                    <p className="font-semibold text-[#222222]">
                      {offer.interest_rate}% p.a.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-[#F8F4EF] p-3 rounded-lg mb-4">
              <p className="text-sm text-[#222222] font-semibold">
                <CheckCircle className="inline mr-2" size={16} />
                Special Benefit: {offer.special_benefit}
              </p>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-sm text-[#444444]">
                <Clock size={16} />
                Valid until: {new Date(offer.valid_until).toLocaleDateString('en-IN')}
              </div>
              
              {!isExpired && (
                <button
                  onClick={() => handleClaimOffer(offer.id)}
                  className="bg-[#354F37] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#426045] transition"
                >
                  Claim Offer
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PreApprovedOffers;
